'use client'
import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
import * as React from 'react'
import { useEffect } from 'react'
import Image from 'next/image'
import Search from '~/app/components/molecules/Search'
import Navigation, { Login } from '~/app/components/organisms/Navigation'
import { urlForImage } from '~/app/lib/sanity.image'
import { FooterType, getFooter } from '~/app/lib/sanity.queries'
import Logo from '../atoms/Logo'

/**
 *
 * @returns: En header, der indeholder logo og søgefunktion.
 * @example: <Header />
 * @alias: Header
 * @module: components/Header
 * @summary: Denne komponent bruges til at vise en header med logo og søgefunktion.
 * @see: src/components/Header.tsx
 * @version: 1.0.0
 * @property: []
 * @todo: Nogen gange er der er der en bug, hvor bruger menuen viser den forkerte state,
 * @author: Kasper Buchholtz
 *
 **/

export default function Header({
  isFirstPageTitle,
}: {
  isFirstPageTitle?: boolean
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [footer, setFooter] = React.useState<FooterType[]>([])
  const [isScrolled, setIsScrolled] = React.useState(false)

  useEffect(() => {
    getFooter().then((nav) => setFooter(nav))
  }, [])

  const handleCloseNav = () => {
    setIsOpen(false)
  }
  const data = footer[0]
  const social = data?.social ?? []

  const handleScroll = () => {
    if (typeof window !== 'undefined' && window.scrollY > 100) {
      setIsScrolled(true)
    } else {
      setIsScrolled(false)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  // if isOpen is true, make the screen unscrollable by setting the body to fixed
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])
  return (
    <>
      <header
        className={` fixed top-0 right-0 w-full py-4 se-grid z-[9999999999] transition-all ${
          isScrolled ? 'bg-lys shadow-md' : ''
        }`}
      >
        <div className="col-span-2 col-start-1 my-auto md:col-span-3 lg:col-span-2 xl:col-span-4">
          <Link href="/">
            <Logo
              className={`w-full h-auto max-w-xs ${isFirstPageTitle ? 'text-lys' : 'text-DabraRød'} ${isScrolled ? '!text-DabraRød' : ''}`}
            />
          </Link>
        </div>
        <div className="flex justify-end my-auto mr-4 -col-start-2 -col-end-2 lg:hidden">
          <Login />
        </div>
        <button
          aria-controls="menu"
          aria-label="Menu"
          onClick={() => setIsOpen(!isOpen)}
          className="relative z-[999999999] lg:hidden my-auto -col-start-1 -col-end-1 size-11.5 group ring-0 ring-DabraRød ring-offset-0 focus-within:ring-2 focus-within:ring-DabraRød focus-within:ring-offset-2"
        >
          <div className="relative w-full h-auto -translate-x-1/2 translate-y-1/2 -right-1/2">
            <span
              className={`block absolute transition-all h-0.5 w-12 ${isFirstPageTitle ? 'bg-lys' : 'bg-DabraRød'} ${isScrolled ? '!bg-DabraRød' : ''} group-hover:bg-DabraRød transform duration-500 ease-in-out ${isOpen ? 'rotate-45 !bg-DabraRød' : '-translate-y-1.5 group-hover:w-10'}`}
              aria-hidden="true"
            ></span>
            <span
              className={`block absolute transition-all h-0.5 w-12 ${isFirstPageTitle ? 'bg-lys' : 'bg-DabraRød'} ${isScrolled ? '!bg-DabraRød' : ''} group-hover:bg-DabraRød transform duration-500 ease-in-out ${isOpen ? 'opacity-0 !bg-DabraRød' : ''}`}
              aria-hidden="true"
            ></span>
            <span
              className={`block absolute transition-all h-0.5 w-12 ${isFirstPageTitle ? 'bg-lys' : 'bg-DabraRød'} ${isScrolled ? '!bg-DabraRød' : ''} group-hover:bg-DabraRød transform duration-500 ease-in-out ${isOpen ? '-rotate-45 !bg-DabraRød' : 'translate-y-1.5 group-hover:w-5'}`}
              aria-hidden="true"
            ></span>
          </div>
        </button>
        <Navigation isFirstPageTitle={isFirstPageTitle} regular />
      </header>
      <AnimatePresence mode="wait">
        {isOpen && <Navigation onClose={handleCloseNav} />}
      </AnimatePresence>
    </>
  )
}
